import { cn } from '@amore/ui/cn'
import { Input } from '@amore/ui/components/input'
import { Search } from 'lucide-react'
import type { ReactNode } from 'react'

export type FilterOption<V extends string> = { value: V; label: string; count?: number }

/**
 * The one filter pattern: chips (single choice, the first is "Vše") plus an
 * optional search. Stat tiles may set the same value. Keep it in the URL on
 * real screens; in the prototype local state is fine.
 */
export function FilterBar<V extends string>({
  filters,
  value,
  onChange,
  search,
  actions,
}: {
  filters: FilterOption<V>[]
  value: V
  onChange: (value: V) => void
  search?: { value: string; onChange: (value: string) => void; placeholder: string }
  /** Extra controls on the right, e.g. a view toggle. */
  actions?: ReactNode
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div role="radiogroup" aria-label="Filtr" className="flex flex-wrap gap-2">
        {filters.map((filter) => {
          const active = filter.value === value
          return (
            // biome-ignore lint/a11y/useSemanticElements: chip styling; radio semantics via role
            <button
              key={filter.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(filter.value)}
              className={cn(
                'inline-flex min-h-9 items-center gap-1.5 rounded-full border px-4 text-xs font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                active
                  ? 'border-action bg-accent text-accent-foreground'
                  : 'border-border bg-card text-foreground/75 hover:bg-muted',
              )}
            >
              {filter.label}
              {filter.count !== undefined ? (
                <span className="tabular-nums text-muted-foreground">{filter.count}</span>
              ) : null}
            </button>
          )
        })}
      </div>
      <span className="flex-1" />
      {search ? (
        <div className="relative w-full sm:w-72">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            type="search"
            value={search.value}
            onChange={(event) => search.onChange(event.target.value)}
            placeholder={search.placeholder}
            aria-label={search.placeholder}
            className="pl-10"
          />
        </div>
      ) : null}
      {actions}
    </div>
  )
}
